import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import { Fetcher } from "../components/Fetcher";
import { getLicenseList } from "../services/api/subscriptionLicenses.api";
import { Pagination } from "../components/Pagination";
import { DEFAULT_PAGING_SIZE } from "../constants/index";
import Grid from "@material-ui/core/Grid";
import { ValueDisplay } from "../components/ValueDisplay";
import { MessageCard } from "../components/MessageCard";
import { LicenseListItemResponse } from "../models/SubscriptionLicenses";
import { DateDisplay } from "../components/DateDisplay";
import colors from "../constants/colors";

import queryString from "query-string";
import { getUrlWithoutQuery } from "../utils";
import { SubscriptionLicenseDetailsDialog } from "../components/SubscriptionLicenseDetailsDialog";

interface SubscriptionLicensesPageProps {
	// product id used to fetch the subscription
	productId: string;
	subscriptionId: string;
}

const useStyles = makeStyles((theme) => ({
	title: {
		marginBottom: theme.spacing(3),
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
	},
	listItem: {
		boxShadow: theme.shadows[1],
		marginBottom: theme.spacing(2),
	},
	listSecondary: {
		marginTop: theme.spacing(0.5),
		fontSize: theme.typography.caption.fontSize,
	},
	details: {
		fontSize: theme.typography.body2.fontSize,
	},
}));

const renderPage = (
	data: LicenseListItemResponse[],
	classes: Record<any, string>,
	setPageNumber: (n: number) => void,
	setRowsPerPage: (n: number) => void,
	pageNumber: number,
	rowsPerPage: number,
	renderDetails: (licenseId: string) => void,
) => {
	const style = {
		marginLeft: 6,
		paddingLeft: 6,
		fontSize: 14,
		cursor: "pointer",
		color: colors.blue400,
		textDecoration: "underline",
	};

	return (
		<div data-testid="subscription-licenses-list">
			{data && data.length > 0 ? (
				<div>
					<List className={classes.list}>
						{data.map((s) => (
							<ListItem key={s.licenseId} className={classes.listItem} data-testid="subscription-licenses-list-item">
								<Grid container justify="space-between">
									<Grid item xs={5}>
										<span
											style={style}
											onClick={() => {
												renderDetails(s.licenseId);
											}}
										>
											{s.licenseId}
										</span>
									</Grid>
									<Grid item xs={4}>
										<ValueDisplay
											label={"Start Date"}
											row={true}
											node={<DateDisplay date={s.startDate}></DateDisplay>}
										/>
									</Grid>
									{s.endDate && (
										<Grid item xs={3}>
											<ValueDisplay label={"End Date"} row={true} node={<DateDisplay date={s.endDate}></DateDisplay>} />
										</Grid>
									)}
								</Grid>
							</ListItem>
						))}
					</List>
					<Pagination
						pageNumber={pageNumber}
						rowsPerPage={rowsPerPage}
						setPageFunction={setPageNumber}
						setRowsPerPageFunction={setRowsPerPage}
						currentPageLength={data.length}
					></Pagination>
				</div>
			) : (
				<MessageCard message="No licenses found." textAlign="left" />
			)}
		</div>
	);
};

/**
 * Fetch and display product subscriptions in a list
 * @param props
 */
export const SubscriptionLicensesPage = (props: SubscriptionLicensesPageProps) => {
	const classes = useStyles();

	const [pageNumber, setPageNumber] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGING_SIZE);
	const requestPageNumber = pageNumber + 1;

	const query = queryString.parse(window.location.search);

	//These variables are used to manage the opening of the TwilioDetails component
	const [detailsOpen, setOpen] = React.useState(query.__licenseId !== undefined || false);
	const [detailsLicenseId, setLicenseId] = React.useState(String(query.__licenseId || ""));

	const renderDetails = (licenseId: string) => {
		const newUrl = getUrlWithoutQuery() + `?__licenseId=${licenseId}`;
		window.history.pushState({}, "", newUrl);
		setLicenseId(licenseId);
		setOpen(true);
	};

	const closeDetails = (open) => {
		window.history.pushState({}, "", getUrlWithoutQuery());
		setOpen(open);
	};

	function renderDetailsPage() {
		if (detailsOpen) {
			return (
				<SubscriptionLicenseDetailsDialog
					productId={props.productId}
					subscriptionId={props.subscriptionId}
					licenseId={detailsLicenseId}
					detailsOpen={detailsOpen}
					setOpen={closeDetails}
				/>
			);
		}
	}

	return (
		<div data-testid={"subscription-licenses-list-page"}>
			<Typography className={classes.title} variant="h4">
				Licenses
			</Typography>
			{renderDetailsPage()}
			{useMemo(() => {
				return (
					<Fetcher
						action={getLicenseList(props.productId, props.subscriptionId, {
							pageNumber: requestPageNumber,
							pageSize: rowsPerPage,
						})}
					>
						{(response) =>
							renderPage(
								response && response.data,
								classes,
								setPageNumber,
								setRowsPerPage,
								pageNumber,
								rowsPerPage,
								renderDetails,
							)
						}
					</Fetcher>
				);
			}, [props.productId, props.subscriptionId, requestPageNumber, rowsPerPage, classes, pageNumber])}
		</div>
	);
};
